import type { RefObject } from 'react';
import {
  type EntityOrSuggestionRef,
  type ExternalContact,
  type System,
  type OrgFunction,
  type User
} from '@bliks-io/contracts';
import {
  EntityPickerBody,
  type EntityPickerBodyHandle,
  type EntityPickerConfig,
  type EntityPickerSuggestNew
} from '@bliks-io/ui';
import type { SuccessorChooserKey } from '@/lib/successorEntityMapping';
import type { SuccessorUpdateData } from '../../../pages/processes/recording/types';
import type { UsePickerSlotSuggestionsResult } from '../hooks/usePickerSlotSuggestions';
import { suggestionPickId, type SuggestionPickEvent } from '../shared/suggestionPickEvent';
import { SuccessorTypeChooserBody } from './SuccessorTypeChooserBody';
import { SuccessorUnclearBody } from './SuccessorUnclearBody';
import { SuccessorNoneBody } from './SuccessorNoneBody';
import { useSuccessorPickerConfigs } from './useSuccessorPickerConfigs';

type EntityType = Exclude<SuccessorChooserKey, 'unclear' | 'none'>;

interface SuccessorPickerPhaseContentProps {
  currentType: SuccessorChooserKey | null;
  pickerConfigs: ReturnType<typeof useSuccessorPickerConfigs>;
  bodyRefs: {
    person: RefObject<EntityPickerBodyHandle | null>;
    team: RefObject<EntityPickerBodyHandle | null>;
    role: RefObject<EntityPickerBodyHandle | null>;
    system: RefObject<EntityPickerBodyHandle | null>;
    externalContact: RefObject<EntityPickerBodyHandle | null>;
  };
  excludeIds: {
    persons: string[];
    orgFunctions: string[];
    systems: string[];
    externalContacts: string[];
  };
  computeSelectedIds: (type: EntityType) => string[];
  onUpdateTentative: (type: EntityType, ids: string[]) => void;
  onViewModeChange: (next: 'bucket' | 'all') => void;
  onSelectType: (next: SuccessorChooserKey | null) => void;
  onExternalContactCreated: (id: string) => void;
  onInlineSave: (update: SuccessorUpdateData) => void;
  /**
   * Notification that the suggest-new dialog persisted (`'created'`) or
   * adopted (`'adopted'`) a suggestion. Consumers narrow on `kind`: reassign
   * flows track creates for the guarded withdraw on cancel and seed their
   * proposed-name lookups from the created doc.
   */
  onSuggestionCreated?: (event: SuggestionPickEvent) => void;
  effectiveHideExternalContact: boolean;
  effectiveHideNone: boolean;
  effectiveHideUnclear: boolean;
  processId?: string;
}

/**
 * OrgFunction and System are the two union-ref slots: their pickers carry the
 * suggest-new dialog, and a save writes `{ type: 'entity' | 'suggestion', id }`
 * refs into the slot named by `slotKey`. One body renders both — only the
 * hook, ref, and slot key differ.
 */
function UnionSlotPickerBody<T extends { _id: string }>({
  typeKey,
  slotKey,
  hook,
  bodyRef,
  excludeIds,
  selectedIds,
  onDraftChange,
  onViewModeChange,
  onInlineSave,
  onSuggestionCreated
}: {
  typeKey: 'team' | 'role' | 'system';
  slotKey: 'orgFunctions' | 'systems';
  hook: {
    items: T[];
    isLoading: boolean;
    config: EntityPickerConfig<T>;
    suggestNew?: EntityPickerSuggestNew;
    renderSuggestDialog: UsePickerSlotSuggestionsResult['renderSuggestDialog'];
  };
  bodyRef: RefObject<EntityPickerBodyHandle | null>;
  excludeIds: string[];
  selectedIds: string[];
  onDraftChange: (ids: string[]) => void;
  onViewModeChange: (next: 'bucket' | 'all') => void;
  onInlineSave: (update: SuccessorUpdateData) => void;
  onSuggestionCreated?: (event: SuggestionPickEvent) => void;
}) {
  const saveRef = (ref: EntityOrSuggestionRef) =>
    onInlineSave(
      slotKey === 'orgFunctions'
        ? { orgFunctions: [ref], successorStatus: '' }
        : { systems: [ref], successorStatus: '' }
    );

  return (
    <>
      <EntityPickerBody<T>
        key={typeKey}
        ref={bodyRef}
        items={hook.items}
        isLoading={hook.isLoading}
        selectedIds={selectedIds}
        mode="single"
        config={hook.config}
        suggestNew={hook.suggestNew}
        excludeIds={excludeIds}
        autofocusSearch={false}
        onDraftChange={onDraftChange}
        onViewModeChange={onViewModeChange}
      />
      {hook.renderSuggestDialog({
        onUseExistingEntity: (next) => {
          if (next[0]) saveRef({ type: 'entity', id: next[0] });
        },
        onSuggestionCreated: (event) => {
          // Notify before the slot write closes the picker; the event stays
          // discriminated so consumers can narrow (withdraw tracking wants
          // creates only). The slot ref is written for both kinds.
          onSuggestionCreated?.(event);
          saveRef({ type: 'suggestion', id: suggestionPickId(event) });
        }
      })}
    </>
  );
}

export function SuccessorPickerPhaseContent({
  currentType,
  pickerConfigs,
  bodyRefs,
  excludeIds,
  computeSelectedIds,
  onUpdateTentative,
  onViewModeChange,
  onSelectType,
  onExternalContactCreated,
  onInlineSave,
  onSuggestionCreated,
  effectiveHideExternalContact,
  effectiveHideNone,
  effectiveHideUnclear,
  processId
}: SuccessorPickerPhaseContentProps) {
  const { personHook, orgFunctionHook, systemHook, externalContactHook } = pickerConfigs;

  switch (currentType) {
    case null:
      return (
        <SuccessorTypeChooserBody
          onSelect={onSelectType}
          hideExternalContact={effectiveHideExternalContact}
          hideNone={effectiveHideNone}
          hideUnclear={effectiveHideUnclear}
        />
      );
    case 'person':
      return (
        <EntityPickerBody<User>
          key="person"
          ref={bodyRefs.person}
          items={personHook.items}
          isLoading={personHook.isLoading}
          selectedIds={computeSelectedIds('person')}
          mode="single"
          config={personHook.config}
          excludeIds={excludeIds.persons}
          autofocusSearch={false}
          onDraftChange={(ids) => onUpdateTentative('person', ids)}
          onViewModeChange={onViewModeChange}
        />
      );
    // Two kind-fixed phases over the ONE orgFunctions slot, served by one
    // kind-following config. They share the exclude list because a picked
    // OrgFunction is excluded regardless of which phase it would appear in.
    // `typeKey`/`bodyRef` stay per-kind so a team↔role crossfade keeps the
    // exiting body's ref from racing the entering one's cleanup.
    case 'team':
    case 'role':
      return (
        <UnionSlotPickerBody<OrgFunction>
          typeKey={currentType}
          slotKey="orgFunctions"
          hook={orgFunctionHook}
          bodyRef={bodyRefs[currentType]}
          excludeIds={excludeIds.orgFunctions}
          selectedIds={computeSelectedIds(currentType)}
          onDraftChange={(ids) => onUpdateTentative(currentType, ids)}
          onViewModeChange={onViewModeChange}
          onInlineSave={onInlineSave}
          onSuggestionCreated={onSuggestionCreated}
        />
      );
    case 'system':
      return (
        <UnionSlotPickerBody<System>
          typeKey="system"
          slotKey="systems"
          hook={systemHook}
          bodyRef={bodyRefs.system}
          excludeIds={excludeIds.systems}
          selectedIds={computeSelectedIds('system')}
          onDraftChange={(ids) => onUpdateTentative('system', ids)}
          onViewModeChange={onViewModeChange}
          onInlineSave={onInlineSave}
          onSuggestionCreated={onSuggestionCreated}
        />
      );
    case 'externalContact':
      return (
        <>
          <EntityPickerBody<ExternalContact>
            key="externalContact"
            ref={bodyRefs.externalContact}
            items={externalContactHook.items}
            isLoading={externalContactHook.isLoading}
            selectedIds={computeSelectedIds('externalContact')}
            mode="single"
            config={externalContactHook.config}
            suggestNew={externalContactHook.suggestNew}
            excludeIds={excludeIds.externalContacts}
            autofocusSearch={false}
            onDraftChange={(ids) => onUpdateTentative('externalContact', ids)}
            onViewModeChange={onViewModeChange}
          />
          {externalContactHook.renderSuggestDialog({
            onCreated: onExternalContactCreated
          })}
        </>
      );
    case 'unclear':
      return <SuccessorUnclearBody processId={processId} />;
    case 'none':
      return <SuccessorNoneBody />;
  }
}
